import { ol_Extent, ol_Feature, ol_source } from "@omnia/exports/openlayers"
import { Geometry } from "ol/geom"

export interface Grid<Zone extends { key: string, geometry: Geometry }, Layer extends { zones: Zone[] }> {

    grid: ExtentGrid
    layer: Layer
    getCells(zoneKey: string): GridCell[]
    getZones(cell: GridCell): Zone[]

}

export function createGrid<
    Zone extends { key: string, geometry: Geometry },
    Layer extends { zones: Zone[] }
>(grid: ExtentGrid, layer: Layer): Grid<Zone, Layer> {


    const source = createVectorSource(layer.zones)

    const zoneMap = new Map<string, GridCell[]>()
    const cellMap = new Map<string, Zone[]>()

    function getCellKey(cell: GridCell) {
        return cell.row + "x" + cell.col
    }

    grid.mapCells(cell => {

        const zones = source.getFeaturesAtCoordinate([cell.x, cell.y])

        if (zones.length) {
            cellMap.set(getCellKey(cell), zones)
        }

        for (const zone of zones) {

            const key = zone.key

            let a = zoneMap.get(key)

            if (!a) {
                a = []
                zoneMap.set(key, a)
            }

            a.push(cell)

        }
    })

    return {
        grid,
        layer,
        getCells: (zoneKey: string) => zoneMap.get(zoneKey),
        getZones: (cell: GridCell) => cellMap.get(getCellKey(cell))
    }


}


export type ExtentGrid = ReturnType<typeof createGridFromExtent>

export interface GridCell {
    col: number
    row: number
    x: number
    y: number
}

export function createGridFromExtent(extent: ol_Extent, resolution = 10) {

    const gridExtent = [
        Math.floor(extent[0] / resolution) * resolution,
        Math.floor(extent[1] / resolution) * resolution,
        Math.ceil(extent[2] / resolution) * resolution,
        Math.ceil(extent[3] / resolution) * resolution,
    ]

    const rows = (gridExtent[3] - gridExtent[1]) / resolution
    const cols = (gridExtent[2] - gridExtent[0]) / resolution

    const rowsArray = [...Array(rows).keys()]
    const colsArray = [...Array(cols).keys()]

    const halfres = resolution / 2

    function mapCells<T>(selector: ({ col, row, x, y }: { col: number, row: number, x: number, y: number }) => T) {

        const result = rowsArray.flatMap(r => {

            const y = gridExtent[1] + (r * resolution) + halfres

            return colsArray.map(c => {

                const x = gridExtent[0] + (c * resolution) + halfres

                return selector({ col: c, row: r, x, y })

            })

        })

        return result

    }

    function getCellExtentFromCenter({ x, y }: { x: number, y: number }) {
        return [
            x - halfres,
            y - halfres,
            x + halfres,
            y + halfres
        ]
    }

    return {
        gridExtent,
        resolution,
        rows,
        cols,
        getCellExtentFromCenter,
        mapCells
    }

}

function createVectorSource<T extends { geometry: Geometry }>(zones: T[]) {

    const features = zones.map(z => {
        const f = new ol_Feature(z.geometry)
        f.set("data", z)
        return f
    })

    const source = new ol_source.Vector({
        useSpatialIndex: true,
        features
    })

    const selectZoneFromFeature = (f: ol_Feature) => f.get("data") as T

    function getFeaturesAtCoordinate(coord: [x: number, y: number]) {
        return source.getFeaturesAtCoordinate(coord).map(selectZoneFromFeature)
    }

    function getFeaturesInCell([x, y]: [x: number, y: number], resolution: number) {
        const r2 = resolution / 2
        const extent = [x - r2, y - r2, x + r2, y + r2]
        return source.getFeaturesInExtent(extent).map(selectZoneFromFeature)
    }

    return {
        getFeaturesAtCoordinate,
        getFeaturesInCell
    }

}
